import { findLast } from 'lodash';
import { useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { AccountAPI, CreateCustomDomain } from 'api/account';
import { useAccount } from 'hooks/use-account';
import {
  GET_CUSTOM_DOMAIN_QUERY_KEY,
  useCustomDomainConfigQuery,
} from 'queries';

export const useCustomDomainActions = () => {
  const queryClient = useQueryClient();

  const { createCustomDomain, verifyCustomDomain } = AccountAPI;
  const { defaultDomainName, primaryDomainName } = useAccount();
  const { data, isLoading, isFetching } = useCustomDomainConfigQuery();

  const customDomainConfig = useMemo(
    () =>
      findLast(data, { primary: true, deleted: false }) ??
      findLast(data, { primary: false, deleted: false }),
    [data],
  );

  const isVerified = defaultDomainName !== primaryDomainName;

  const addFunction = (payload: CreateCustomDomain) =>
    createCustomDomain(payload);
  const { mutate: addMutation, isLoading: isAdding } = useMutation(addFunction);

  const onAdd = (customDomainName: string) => {
    if (customDomainName) {
      addMutation(
        { customDomainName },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(GET_CUSTOM_DOMAIN_QUERY_KEY);
          },
        },
      );
    }
  };

  const verifyFunction = (payload: CreateCustomDomain) =>
    verifyCustomDomain(payload);
  const {
    mutate: verifyMutation,
    isLoading: isVerifying,
    data: verificationStatus,
    isError: isValidationError,
  } = useMutation(verifyFunction);

  const onVerify = () => {
    if (customDomainConfig) {
      verifyMutation(
        { customDomainName: customDomainConfig.domainName },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(GET_CUSTOM_DOMAIN_QUERY_KEY);
          },
        },
      );
    }
  };

  const refetchData = () => {
    queryClient.invalidateQueries(GET_CUSTOM_DOMAIN_QUERY_KEY);
  };

  return {
    customDomainConfig,
    defaultDomainName,
    primaryDomainName,
    verificationStatus,
    isLoading,
    isFetching,
    isVerifying,
    isValidationError,
    isVerified,
    isAdding,
    onAdd,
    onVerify,
    refetchData,
  };
};
